import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { IndianRupee, Receipt, RotateCcw, Undo2 } from 'lucide-react';
import type { Alteration, Payment } from '@tailoring-crm/shared';
import { alterationPaymentsPath } from '../../lib/alterations/alterations-api.js';
import {
  useAlterationPayments,
  useReverseAlterationPayment,
} from '../../lib/alterations/use-alterations.js';
import { useAuth } from '../../lib/auth/auth-context.js';
import { formatCurrency, formatDate } from '../../lib/format.js';
import { ReceiptDialog } from '../payments/receipt-dialog.js';
import { RecordPaymentDialog } from '../payments/record-payment-dialog.js';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  useToast,
} from '@tailoring-crm/ui';

/**
 * The money on one alteration — its own balance, never its order's.
 *
 * The order's payments card, cut down to what an alteration needs: the charge, what has been
 * paid, what is left, and the trail of entries with the reversed ones still showing. Taking money
 * uses the same dialog (and the same offline queue) as an order, pointed at this ledger.
 *
 * A tailor sees none of it, as on an order: every payment route refuses them.
 */
export function AlterationPaymentsCard({
  alteration,
}: {
  alteration: Alteration;
}): ReactElement | null {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { role } = useAuth();

  const ledger = useAlterationPayments(role === 'tailor' ? null : alteration.id);
  const reverse = useReverseAlterationPayment(alteration.id);

  const [collecting, setCollecting] = useState<'payment' | 'refund' | null>(null);
  const [receipt, setReceipt] = useState<Payment | null>(null);
  const [reversing, setReversing] = useState<Payment | null>(null);
  const [reason, setReason] = useState('');

  if (role === 'tailor') return null;

  const view = ledger.data;
  const paidPaise = view?.paidPaise ?? alteration.amountPaidPaise;
  const outstandingPaise = view?.outstandingPaise ?? alteration.outstandingPaise;
  const creditPaise = view?.creditPaise ?? 0;

  const onReverse = async (): Promise<void> => {
    if (!reversing) return;
    try {
      await reverse.mutateAsync({ paymentId: reversing.id, reason: reason.trim() });
      toast({ title: t('payments.reversed'), variant: 'success' });
      setReversing(null);
      setReason('');
    } catch (error) {
      toast({
        title: error instanceof Error ? error.message : t('error.unexpected'),
        variant: 'destructive',
      });
    }
  };

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-border p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">{t('payments.heading')}</h3>
          <p className="text-sm text-muted-foreground">
            {alteration.chargePaise === 0
              ? t('alterations.detail.free')
              : creditPaise > 0
                ? t('payments.creditHeld', { amount: formatCurrency(creditPaise) })
                : outstandingPaise > 0
                  ? t('payments.outstandingIs', { amount: formatCurrency(outstandingPaise) })
                  : t('payments.fullySettled')}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {alteration.status !== 'cancelled' && outstandingPaise > 0 ? (
            <Button size="sm" onClick={() => setCollecting('payment')}>
              <IndianRupee aria-hidden="true" />
              {t('payments.record')}
            </Button>
          ) : null}
          {paidPaise > 0 ? (
            <Button variant="outline" size="sm" onClick={() => setCollecting('refund')}>
              <Undo2 aria-hidden="true" />
              {t('payments.refund')}
            </Button>
          ) : null}
        </div>
      </div>

      <dl className="grid grid-cols-3 gap-3 text-sm">
        <div className="rounded-md bg-muted/50 p-2">
          <dt className="text-xs text-muted-foreground">{t('alterations.field.charge')}</dt>
          <dd className="font-semibold tabular-nums">{formatCurrency(alteration.chargePaise)}</dd>
        </div>
        <div className="rounded-md bg-muted/50 p-2">
          <dt className="text-xs text-muted-foreground">{t('orders.paid')}</dt>
          <dd className="font-semibold tabular-nums">{formatCurrency(paidPaise)}</dd>
        </div>
        <div className="rounded-md bg-muted/50 p-2">
          <dt className="text-xs text-muted-foreground">{t('orders.balance')}</dt>
          <dd className="font-semibold tabular-nums">
            {outstandingPaise > 0 ? formatCurrency(outstandingPaise) : t('orders.settled')}
          </dd>
        </div>
      </dl>

      {view && view.entries.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {view.entries.map((entry) => {
            const reversed = entry.reversedByPaymentId !== null;
            const isReversal = entry.kind === 'reversal';
            return (
              <li
                key={entry.id}
                className="flex flex-wrap items-start justify-between gap-2 rounded-md border border-border p-2 text-sm"
              >
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2 font-medium">
                    <span className={reversed ? 'line-through' : undefined}>
                      {entry.signedAmountPaise < 0 ? '−' : ''}
                      {formatCurrency(entry.amountPaise)}
                    </span>
                    <span className="text-muted-foreground">
                      {t(`payments.methods.${entry.method}`)}
                    </span>
                    {isReversal ? <Badge variant="warning">{t('payments.reversal')}</Badge> : null}
                    {entry.kind === 'refund' ? (
                      <Badge variant="secondary">{t('payments.refundBadge')}</Badge>
                    ) : null}
                    {reversed ? (
                      <Badge variant="warning">{t('payments.reversedBadge')}</Badge>
                    ) : null}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDate(entry.receivedAt)}
                    {entry.recordedByName ? ` · ${entry.recordedByName}` : ''}
                    {entry.reference ? ` · ${entry.reference}` : ''}
                  </p>
                  {entry.reason ? (
                    <p className="text-xs text-muted-foreground">
                      {t('payments.reversalReason', { reason: entry.reason })}
                    </p>
                  ) : null}
                </div>

                <div className="flex shrink-0 gap-1">
                  {!isReversal ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('payments.receiptFor', {
                        amount: formatCurrency(entry.amountPaise),
                      })}
                      onClick={() => setReceipt(entry)}
                    >
                      <Receipt aria-hidden="true" />
                    </Button>
                  ) : null}
                  {/* Undoing an entry is the owner's alone, as on an order. */}
                  {!isReversal && !reversed && role === 'owner' ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('payments.reverseFor', {
                        amount: formatCurrency(entry.amountPaise),
                      })}
                      onClick={() => setReversing(entry)}
                    >
                      <RotateCcw aria-hidden="true" />
                    </Button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      ) : alteration.chargePaise > 0 ? (
        <p className="text-sm text-muted-foreground">{t('payments.noneYet')}</p>
      ) : null}

      {collecting ? (
        <RecordPaymentDialog
          paymentsPath={alterationPaymentsPath(alteration.id)}
          number={alteration.number}
          kind={collecting}
          totalPaise={alteration.chargePaise}
          paidPaise={paidPaise}
          outstandingPaise={outstandingPaise}
          suggestedMethod={view?.suggestedMethod ?? 'cash'}
          onClose={() => setCollecting(null)}
          onRecorded={(payment) => setReceipt(payment)}
        />
      ) : null}

      {receipt && view ? (
        // The freshest ledger, so a receipt opened straight after recording shows that entry.
        <ReceiptDialog payment={receipt} payments={view} onClose={() => setReceipt(null)} />
      ) : null}

      <Dialog open={reversing !== null} onOpenChange={(open) => !open && setReversing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('payments.reverseTitle', {
                amount: formatCurrency(reversing?.amountPaise ?? 0),
              })}
            </DialogTitle>
            <DialogDescription>{t('payments.reverseHint')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="alteration-reversal-reason">{t('payments.reverseReasonLabel')}</Label>
            <Input
              id="alteration-reversal-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReversing(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={reason.trim().length < 3 || reverse.isPending}
              onClick={() => void onReverse()}
            >
              {t('payments.reverseConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

export default AlterationPaymentsCard;
